import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { deleteSingle, deleteSelected, fetchBatches, fetchGestureCounts, getExpectedTotalForCategory } from "../../utils/TrainingHelpers";
import { findGestureCategory } from "../../utils/GestureCategories";
import BatchItem from './BatchItem';
import PhraseBatchItem from './PhraseTraining/PhraseBatchItem';
import CountList from './CountList';
import GestureFilter from './GestureFilter';

// Batches created before this date should be ignored when showing incomplete batches
const INCOMPLETE_BATCH_CUTOFF = new Date('2025-04-01T00:00:00Z');

const VideoList = forwardRef(({ 
    setError,
    selectedVideo,
    setSelectedVideo,
    setShowCamera,
    viewAllUsers,
    setViewAllUsers,
    gestureCounts,
    totalCount,
    updateGestureCounts,
    isAdmin,
    handleRecordingProcess,
    selectedFilters,
    setSelectedFilters,
    showFilterPopup,
    setShowFilterPopup,
    tempFilters,
    setTempFilters,
    gestures,
    isBatchActive,
    lockedBatchId,
    isAdrian,
    onAddReplacements,
    onEditRating = () => {},
    users,
    selectedUser,
    setSelectedUser,
    currentUserId,
    page = 'training'
}, ref) => {
    const [batches, setBatches] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedVideos, setSelectedVideos] = useState(new Set());
    // Track ongoing fetch requests to avoid race conditions when switching tabs
    const fetchIdRef = useRef(0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmData, setDeleteConfirmData] = useState({ type: '', count: 0, videoId: null });
    const [viewMode, setViewMode] = useState('videos');
    const [batchTab, setBatchTab] = useState('all'); // 'all' or 'incomplete'
    const initialLoadRef = useRef(true);
    const BATCHES_PER_PAGE = 20;
    const loadingRef = useRef(null);
    const observerRef = useRef(null);
    const batchItemsRef = useRef(new Map());
    const filterRef = useRef(null);
    const [filterPosition, setFilterPosition] = useState({ x: 0, y: 0 });

    const phraseGroups = useMemo(() => {
        if (page !== 'phrase') return {};
        return batches
            .filter(batch => batch.videoCount > 0)
            .reduce((acc, batch) => {
                const [phraseId, variation] = batch.gesture.split('_');
                if (!acc[phraseId]) acc[phraseId] = {};
                if (!acc[phraseId][batch.username]) acc[phraseId][batch.username] = [];
                acc[phraseId][batch.username].push({ variation, batchId: batch.batchId, videoCount: batch.videoCount });
                return acc;
            }, {});
    }, [batches, page]);

    // Reset to 'all' tab when viewAllUsers changes
    useEffect(() => {
        setBatchTab('all');
    }, [viewAllUsers]);

    const isBatchIncomplete = useCallback((batch) => {
        const category = findGestureCategory(batch.gesture);
        if (!category) return false;
        const isAdrianUser = batch.username === 'adrian';
        const expected = getExpectedTotalForCategory(category.id, isAdrianUser);
        const batchStart = new Date(batch.firstTimestamp);
        return (
            !batch.isLegacy &&
            batch.videoCount < expected &&
            batchStart >= INCOMPLETE_BATCH_CUTOFF
        );
    }, []);

    const refreshBatches = useCallback(async () => {
        console.log('Refreshing batch list');
        const fetchId = ++fetchIdRef.current;
        setIsLoading(true);
        try {
            const result = await fetchBatches(
                () => {},
                setError,
                viewAllUsers,
                0,
                BATCHES_PER_PAGE,
                Array.from(selectedFilters),
                selectedUser,
                batchTab === 'incomplete' ? INCOMPLETE_BATCH_CUTOFF : null,
                page
            );
            if (fetchId !== fetchIdRef.current) return;
            if (result.success) {
                let fetched = result.batches;
                if (batchTab === 'incomplete') {
                    fetched = fetched.filter(isBatchIncomplete);
                }
                setBatches(fetched);
                setOffset(BATCHES_PER_PAGE);
                setHasMore(result.pagination.totalCount > BATCHES_PER_PAGE);
            }
        } catch (error) {
            if (fetchId === fetchIdRef.current) {
                console.error('Error refreshing batches:', error);
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [viewAllUsers, setError, selectedFilters, selectedUser, BATCHES_PER_PAGE, batchTab, isBatchIncomplete, page]);

    const refreshVisibleBatches = useCallback(async () => {
        if (offset === 0) return;
        const fetchId = ++fetchIdRef.current;
        setIsLoading(true);
        try {
            const result = await fetchBatches(
                () => {},
                setError,
                viewAllUsers,
                0,
                offset,
                Array.from(selectedFilters),
                selectedUser,
                batchTab === 'incomplete' ? INCOMPLETE_BATCH_CUTOFF : null,
                page
            );
            if (fetchId !== fetchIdRef.current) return;
            if (result.success) {
                let fetched = result.batches;
                if (batchTab === 'incomplete') {
                    fetched = fetched.filter(isBatchIncomplete);
                }
                setBatches(fetched);
                setHasMore(result.pagination.totalCount > offset);
            }
        } catch (error) {
            if (fetchId === fetchIdRef.current) {
                console.error('Error refreshing visible batches:', error);
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [offset, viewAllUsers, setError, selectedFilters, selectedUser, batchTab, isBatchIncomplete, page]);

    const refreshBatch = useCallback((batchId) => {
        const batchRef = batchItemsRef.current.get(batchId);
        if (batchRef && batchRef.refreshVideos) {
            batchRef.refreshVideos();
        }
    }, []);

    useImperativeHandle(ref, () => ({
        refreshBatches,
        refreshVisibleBatches,
        refreshBatch
    }));

    // Initial fetch of batches
    useEffect(() => {
        let isMounted = true;
        const loadBatches = async () => {
            if (!isMounted) return;
            const fetchId = ++fetchIdRef.current;
            setIsLoading(true);
            try {
                const result = await fetchBatches(
                    () => {},
                    setError,
                    viewAllUsers,
                    0,
                    BATCHES_PER_PAGE,
                    Array.from(selectedFilters),
                    selectedUser,
                    batchTab === 'incomplete' ? INCOMPLETE_BATCH_CUTOFF : null,
                    page
                );
                if (fetchId !== fetchIdRef.current) return;
                if (result.success) {
                    let fetched = result.batches;
                    if (batchTab === 'incomplete') {
                        fetched = fetched.filter(isBatchIncomplete);
                    }
                    setBatches(fetched);
                    setOffset(BATCHES_PER_PAGE);
                    setHasMore(result.pagination.totalCount > BATCHES_PER_PAGE);
                }
            } catch (error) {
                if (fetchId === fetchIdRef.current) {
                    console.error(error);
                }
            } finally {
                if (isMounted && fetchId === fetchIdRef.current) {
                    setIsLoading(false);
                    initialLoadRef.current = false;
                }
            }
        };
        loadBatches();
        return () => { isMounted = false; };
    }, [viewAllUsers, setError, selectedFilters, selectedUser, BATCHES_PER_PAGE, batchTab, isBatchIncomplete, page]);

    // Add a separate effect for filter changes
    useEffect(() => {
        if (!initialLoadRef.current) {
            setOffset(0);
            refreshBatches();
        }
    }, [selectedFilters, selectedUser, batchTab, refreshBatches]);

    // Infinite scrolling for batches
    const loadMoreBatches = async () => {
        if (isLoading || !hasMore) return;
        const fetchId = ++fetchIdRef.current;
        setIsLoading(true);
        try {
            const result = await fetchBatches(
                () => {},
                setError,
                viewAllUsers,
                offset,
                BATCHES_PER_PAGE,
                Array.from(selectedFilters),
                selectedUser,
                batchTab === 'incomplete' ? INCOMPLETE_BATCH_CUTOFF : null,
                page
            );
            if (fetchId !== fetchIdRef.current) return;
            if (result.success) {
                let fetched = result.batches;
                if (batchTab === 'incomplete') {
                    fetched = fetched.filter(isBatchIncomplete);
                }
                setBatches(prev => [...prev, ...fetched]);
                const newOffset = offset + BATCHES_PER_PAGE;
                setOffset(newOffset);
                setHasMore(result.pagination.totalCount > newOffset);
            }
        } catch (error) {
            if (fetchId === fetchIdRef.current) {
                console.error(error);
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                setIsLoading(false);
            }
        }
    };

    // Set up intersection observer for infinite scrolling
    useEffect(() => {
        if (!loadingRef.current) return;
        const options = {
            root: null,
            rootMargin: '20px',
            threshold: 0.1
        };
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !isLoading && viewMode === 'videos') {
                loadMoreBatches();
            }
        }, options);
        observer.observe(loadingRef.current);
        observerRef.current = observer;
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, isLoading, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleVideoSelect = (videoId) => {
        setSelectedVideos(prev => {
            const newSet = new Set(prev);
            if (newSet.has(videoId)) {
                newSet.delete(videoId);
            } else {
                newSet.add(videoId);
            }
            return newSet;
        });
    };

    const handleVideoClick = (video, e) => {
        // Prevent video selection when clicking checkbox or delete button
        if (e.target.type === 'checkbox' || e.target.closest('.delete-button')) {
            return;
        }
        setSelectedVideo(video);
        setShowCamera(false);
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirmData.type === 'single') {
            await deleteSingle(deleteConfirmData.videoId, setError, async () => {
                // Get the batch reference and update its state
                const batchRef = batchItemsRef.current.get(deleteConfirmData.batchId);
                if (batchRef) {
                    batchRef.decrementCount();
                    batchRef.removeVideo(deleteConfirmData.videoId);
                }

                // Update the batches list if the batch is now empty
                setBatches(prev => prev.filter(batch => {
                    if (batch.batchId === deleteConfirmData.batchId) {
                        return batch.videoCount > 1; // Keep if it had more than one video
                    }
                    return true;
                }));
                try {
                    const data = await fetchGestureCounts(viewAllUsers, selectedUser || null, page);
                    updateGestureCounts(null, 0, data.counts, data.total);
                } catch (err) {
                    console.error('Error updating gesture counts after delete:', err);
                }
            });
        } else if (deleteConfirmData.type === 'batch') {
            await deleteSelected(selectedVideos, setSelectedVideos, setError, async () => {
                // Group selected videos by batch using batchId parsed from the videoId
                const videosByBatch = new Map();
                Array.from(selectedVideos).forEach(videoId => {
                    const batchId = videoId.split('-')[0];
                    if (!videosByBatch.has(batchId)) {
                        videosByBatch.set(batchId, []);
                    }
                    videosByBatch.get(batchId).push(videoId);
                });

                // Update each affected batch
                videosByBatch.forEach((videoIds, batchId) => {
                    const batchRef = batchItemsRef.current.get(String(batchId));
                    if (batchRef) {
                        batchRef.decrementCountByAmount(videoIds.length);
                        batchRef.removeVideos(videoIds);
                    }
                });

                // Update the batches list, removing empty batches
                setBatches(prev => {
                    return prev.filter(batch => {
                        const selectedFromBatch = videosByBatch.get(String(batch.batchId))?.length || 0;
                        return batch.videoCount > selectedFromBatch;
                    });
                });
                try {
                    const data = await fetchGestureCounts(viewAllUsers, selectedUser || null, page);
                    updateGestureCounts(null, 0, data.counts, data.total);
                } catch (err) {
                    console.error('Error updating gesture counts after delete:', err);
                }
            });
        }
        setShowDeleteConfirm(false);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setDeleteConfirmData({ type: '', count: 0, videoId: null });
    };

    const initiateDelete = (type, count, videoId = null, batchId = null) => {
        setDeleteConfirmData({ type, count, videoId, batchId });
        setShowDeleteConfirm(true);
    };

    // Add this new function to handle view mode toggle
    const toggleViewMode = () => {
        setViewMode(prev => prev === 'videos' ? 'counts' : 'videos');
    };

    return (
        <div className={`my-videos-section${isBatchActive ? ' batch-active' : ''}`}>
            {isBatchActive && (
                <div className="batch-active-overlay">
                    <div className="batch-active-message">
                        <img src="/icons/lock.svg" alt="Locked" className="icon" />
                        Recording batch in progress...
                    </div>
                </div>
            )}
            <div className="videos-header">
                <h3>{viewAllUsers ? "All User Data" : "My Data"}</h3>
                {page !== 'phrase' && (
                    <div className="batch-tabs">
                        <button
                            className={batchTab === 'all' ? 'active' : ''}
                            onClick={() => { setBatchTab('all'); setOffset(0); }}
                        >
                            All Batches
                        </button>
                        <button
                            className={batchTab === 'incomplete' ? 'active' : ''}
                            onClick={() => { setBatchTab('incomplete'); setOffset(0); }}
                        >
                            Incomplete Batches
                        </button>
                    </div>
                )}
                <div className="videos-actions">
                    <div className="videos-controls">
                        {isAdmin && (
                            <div className={`firewall-mode-toggle admin-view-toggle${isLoading ? ' disabled' : ''}`}>
                                <label className="toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={viewAllUsers}
                                        onChange={(e) => setViewAllUsers(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                    <span className="toggle-text">View All User Data</span>
                                    <div className="toggle-tooltip" title="Toggle between viewing all users' data or just your own. This may take longer to load when active.">ⓘ</div>
                                </label>
                            </div>
                        )}
                        {viewAllUsers && (
                            <select
                                className="user-filter-select"
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                            >
                                <option value="">All Users</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                            </select>
                        )}
                        <GestureFilter
                            showFilterPopup={showFilterPopup}
                            setShowFilterPopup={setShowFilterPopup}
                            tempFilters={tempFilters}
                            setTempFilters={setTempFilters}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            gestures={gestures}
                            filterPosition={filterPosition}
                            setFilterPosition={setFilterPosition}
                            filterRef={filterRef}
                            page={page}
                        />
                        {page !== 'phrase' && (
                            <button
                                className="action-button view-counts-button"
                                onClick={toggleViewMode}
                                title={viewMode === 'videos' ? "Show gesture statistics" : "Show recorded videos"}
                            >
                                {viewMode === 'videos' ? (
                                    <>
                                        <img src="/icons/chart-bar.svg" alt="Chart" className="icon" />
                                        View Stats
                                    </>
                                ) : (
                                    <>
                                        <img src="/icons/video-icon.svg" alt="Videos" className="icon" />
                                        View Videos
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    {viewMode === 'videos' && selectedVideos.size > 0 && (
                        <button
                            onClick={() => initiateDelete('batch', selectedVideos.size)}
                            className="delete-selected-button"
                        >
                            Delete {selectedVideos.size} Video{selectedVideos.size > 1 ? 's' : ''}
                        </button>
                    )}
                </div>
            </div>
            {/* Add delete confirmation popup */}
            {showDeleteConfirm && (
                <div className="delete-confirm-overlay">
                    <div className="delete-confirm-popup">
                        <h4>Confirm Delete</h4>
                        <p>Are you sure you want to delete {deleteConfirmData.count} video{deleteConfirmData.count > 1 ? 's' : ''}?</p>
                        <div className="delete-confirm-buttons">
                            <button onClick={handleDeleteConfirm} className="confirm-delete">Delete</button>
                            <button onClick={handleDeleteCancel} className="cancel-delete">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            {viewMode === 'videos' ? (
                <div className="video-list">
                    {batches.length === 0 && isLoading ? (
                        <div className="loading-container">
                            <span>Loading batches...</span>
                            <div className="loading-spinner">
                                <div className="spinner"></div>
                            </div>
                        </div>
                    ) : batches.length > 0 ? (
                        <>
                            {page === 'phrase'
                                ? Object.entries(phraseGroups).flatMap(([phraseId, userGroups]) =>
                                    Object.entries(userGroups).map(([username, variations]) => (
                                        <PhraseBatchItem
                                            key={`${phraseId}-${username}`}
                                            phraseId={phraseId}
                                            recordingUser={username}
                                            variations={variations}
                                            setError={setError}
                                            viewAllUsers={viewAllUsers}
                                            onVideoSelect={handleVideoSelect}
                                            selectedVideos={selectedVideos}
                                            onVideoClick={handleVideoClick}
                                            onVideoDelete={(videoId, batchId) => initiateDelete('single', 1, videoId, batchId)}
                                            onEditRating={onEditRating}
                                            onAddReplacements={onAddReplacements}
                                            isAdrian={isAdrian}
                                            isLocked={variations.some(v => v.batchId === lockedBatchId)}
                                            currentUserId={currentUserId}
                                            page={page}
                                            ref={ref => {
                                                if (ref) {
                                                    variations.forEach(v => batchItemsRef.current.set(v.batchId, ref));
                                                } else {
                                                    variations.forEach(v => batchItemsRef.current.delete(v.batchId));
                                                }
                                            }}
                                        />
                                    ))
                                )
                                : batches
                                    .filter(batch => batch.videoCount > 0)
                                    .map(batch => (
                                        <BatchItem
                                            key={batch.batchId}
                                            batch={batch}
                                            setError={setError}
                                            viewAllUsers={viewAllUsers}
                                            onVideoSelect={handleVideoSelect}
                                            selectedVideos={selectedVideos}
                                            onVideoClick={handleVideoClick}
                                            onVideoDelete={(videoId) => initiateDelete('single', 1, videoId, batch.batchId)}
                                            onAddReplacements={onAddReplacements}
                                            isAdrian={viewAllUsers ? batch.username === 'adrian' : isAdrian}
                                            isLocked={lockedBatchId === batch.batchId}
                                            selectedUser={selectedUser}
                                            currentUserId={currentUserId}
                                            page={page}
                                            ref={ref => {
                                                if (ref) {
                                                    batchItemsRef.current.set(batch.batchId, ref);
                                                } else {
                                                    batchItemsRef.current.delete(batch.batchId);
                                                }
                                            }}
                                        />
                                    ))}
                            {hasMore && (
                                <div ref={loadingRef} className="scroll-loader">
                                    {isLoading && (
                                        <div className="loading-spinner small">
                                            <div className="spinner"></div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="no-batches-message">No batches found</div>
                    )}
                </div>
            ) : (
                <CountList 
                    gestureCounts={gestureCounts}
                    totalCount={totalCount}
                    viewAllUsers={viewAllUsers}
                />
            )}
        </div>
    );
});

export default VideoList; 